import { GenericCartItem } from '@contexts/GenericCartContext/genericCart.types';
import { useGenericCart } from '@contexts/GenericCartContext/GenericCartContext';
import { showToast } from '@design-system';
import { GetUsersResponse, useCreateEpiOrderMutation, useGetUsersMutation } from '@services';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const useEpiCartPageVM = () => {
    const { items, removeItem, updateQuantity, getTotalItems, clearCart, saveCart } = useGenericCart({ type: 'epi' });
    const navigate = useNavigate();

    const [showModal, setShowModal] = useState<boolean>(false);
    const [cartName, setCartName] = useState<string>('');

    const [operators, setOperators] = useState<GetUsersResponse['operators']>([]);
    const [operatorsOptions, setOperatorsOptions] = useState<{ id: string; label: string }[]>([]);
    const [worksitesOptions, setWorksitesOptions] = useState<{ id: string; label: string }[]>([]);
    const [selectedOperatorId, setSelectedOperatorId] = useState<string>('');
    const [selectedWorksiteId, setSelectedWorksiteId] = useState<string>('');
    const [deliveryDate, setDeliveryDate] = useState<string>('');
    const [comment, setComment] = useState<string>('');

    const actions = [
        {
            label: 'Vider le panier',
            onClick: () => clearCart()
        },
        {
            label: 'Enregistrer le panier dans les favoris',
            onClick: () => setShowModal(true)
        }
    ];

    const [getUsers, { data: users }] = useGetUsersMutation();
    const [createEpiOrder, { isLoading: isCreatingOrder, isSuccess: isOrderCreated, data: createdOrder, isError: isOrderError }] = useCreateEpiOrderMutation();

    const handleOperatorChange = (operatorId: string) => {
        setSelectedOperatorId(operatorId);
        setSelectedWorksiteId('');

        const selectedOperator = operators.find((op) => op.id_operator === operatorId);
        if (selectedOperator) {
            const worksites = selectedOperator.worksites.map((worksite) => ({
                id: worksite.id,
                label: worksite.name
            }));
            setWorksitesOptions(worksites);
        } else {
            setWorksitesOptions([]);
        }
    };

    const handleSaveCart = async () => {
        const response = await saveCart(cartName, items);
        if (response === 'success') {
            setShowModal(false);
            setCartName('');
            showToast({
                message: 'Panier EPI enregistré avec succès',
                type: 'success'
            });
        } else {
            showToast({
                message: "Une erreur est survenue lors de l'enregistrement du panier EPI",
                type: 'neutral'
            });
        }
    };

    const isOrderable = useMemo(() => {
        return items.length > 0 && selectedOperatorId && selectedWorksiteId && deliveryDate;
    }, [items, selectedOperatorId, selectedWorksiteId, deliveryDate]);

    const handleCreateOrder = async () => {
        if (isCreatingOrder) return;
        createEpiOrder(formatCreateEpiOrderPayload(items));
    };

    const formatCreateEpiOrderPayload = (items: GenericCartItem[]) => {
        const products = items.map((item) => ({
            id: parseInt(item.id),
            quantity: item.quantity
        }));

        return {
            operator_id: parseInt(selectedOperatorId),
            id_worksite: parseInt(selectedWorksiteId),
            date_order: deliveryDate,
            comment,
            products
        };
    };

    useEffect(() => {
        getUsers(undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (users) {
            setOperators(users.operators);
            const operatorsOpts = users.operators.map((op) => ({
                id: op.id_operator,
                label: op.name
            }));
            setOperatorsOptions(operatorsOpts);
        }
    }, [users]);

    useEffect(() => {
        if (isOrderCreated && createdOrder) {
            showToast({
                message: 'Demande EPI créée avec succès',
                type: 'success'
            });

            clearCart();
            navigate('/sante-securite/epi/orders');
        }

        if (isOrderError) {
            showToast({
                message: 'Une erreur est survenue lors de la création de la demande EPI',
                type: 'neutral'
            });
        }
    }, [isOrderCreated, isOrderError, createdOrder, clearCart, navigate]);

    return {
        actions,
        cartName,
        comment,
        deliveryDate,
        getTotalItems,
        handleOperatorChange,
        handleSaveCart,
        isOrderable,
        items,
        operatorsOptions,
        removeItem,
        selectedOperatorId,
        selectedWorksiteId,
        setCartName,
        setShowModal,
        updateQuantity,
        worksitesOptions,
        showModal,
        setSelectedWorksiteId,
        setDeliveryDate,
        setComment,
        handleCreateOrder,
        isCreatingOrder
    };
};
